import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postService } from '../../services/postService';
import { categoryService } from '../../services/categoryService';
import DataTable from '../components/common/DataTable';
import Pagination from '../components/common/Pagination';
import InfoModal from '../components/common/InfoModal';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import ToastNotification from '../components/common/ToastNotification';


function PostList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Modal states
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch posts when page, pageSize, or filters change
  useEffect(() => {
    fetchPosts();
  }, [currentPage, pageSize, selectedCategory, debouncedSearch]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await postService.getPaginatedAdminPosts(
        currentPage, 
        pageSize, 
        selectedCategory || null, 
        debouncedSearch
      );
      
      if (response.success) {
        setPosts(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleAdd = () => {
    navigate('/admin/posts/create');
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setShowInfoModal(true);
  };

  const handleEdit = (post) => {
    navigate(`/admin/posts/edit/${post.id}`);
  };

  const handleContentBlocks = (post) => {
    navigate(`/admin/posts/${post.id}/content`);
  };

  const handleDeleteClick = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await postService.delete(selectedPost.id);
      showNotification('Post deleted successfully');
      setShowDeleteModal(false);
      setSelectedPost(null);
      
      if (posts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchPosts();
      }
    } catch (err) {
      showNotification(err.message, 'danger');
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const getCategoryName = (categoryName) => {
    return categoryName || 'Uncategorized';
  };

  const columns = [
    { 
      key: 'id', 
      header: 'ID',
      render: (value) => <span style={{ fontWeight: '500' }}>#{value}</span>
    },
    { 
      key: 'title', 
      header: 'Title',
      render: (value, item) => (
        <div>
          <div style={{ fontWeight: '500' }}>{value}</div>
          <small className="admin-crud-empty-subtext">
            {item.description?.substring(0, 60)}...
          </small>
        </div>
      )
    },
    { 
      key: 'category', 
      header: 'Category',
      render: (value) => getCategoryName(value)
    },
    { 
      key: 'isTutorials', 
      header: 'Type',
      render: (value) => (
        <span className={`admin-crud-badge ${value ? 'admin-crud-badge-success' : 'admin-crud-badge-info'}`}>
          {value ? '📘Tutorial' : '📝 Blog'}
        </span>
      )
    },
    { 
      key: 'isPublished', 
      header: 'Status',
      render: (value) => (
        <span className={`admin-crud-badge ${value ? 'admin-crud-badge-success' : 'admin-crud-badge-warning'}`}>
          {value ? 'Published' : '📝 Draft'}
        </span>
      )
    },
    { 
      key: 'createdAt', 
      header: 'Date',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const infoFields = [
    { key: 'id', label: 'Post ID' },
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
    { 
      key: 'isTutorials', 
      label: 'Type',
      render: (value) => value ? 'Tutorial' : 'Blog'
    },
    { 
      key: 'isPublished', 
      label: 'Status',
      render: (value) => value ? 'Published' : 'Draft'
    },
    { key: 'coverImage', label: 'Cover Image' },
    { key: 'createdAt', label: 'Created Date' }
  ];

  const renderCustomActions = (post) => (
    <div className="admin-crud-actions-group">
      <button
        className="admin-crud-action-btn admin-crud-action-view"
        onClick={() => handleView(post)}
        title="View Details"
      >
        👁️ View
      </button>
      <button
        className="admin-crud-action-btn"
        style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}
        onClick={() => handleContentBlocks(post)}
        title="Manage Content Blocks"
      >
        📦 Blocks
      </button>
      <button
        className="admin-crud-action-btn admin-crud-action-edit"
        onClick={() => handleEdit(post)}
        title="Edit Post"
      >
        ✏️ Edit
      </button>
      <button
        className="admin-crud-action-btn admin-crud-action-delete"
        onClick={() => handleDeleteClick(post)}
        title="Delete Post"
      >
        🗑️ Delete
      </button>
    </div>
  );

  if (loading && !posts.length) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        {error && (
          <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
            <div className="admin-crud-toast-content admin-crud-toast-danger">
              <div className="admin-crud-toast-header">
                <strong>Error</strong>
                <button onClick={() => setError(null)}>✕</button>
              </div>
              <div className="admin-crud-toast-body">{error}</div>
            </div>
          </div>
        )}

        {/* Filter Section */}
        <div className="admin-filters-section" style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          background: '#f7fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div className="admin-filter-group">
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>🏷️ Category:</label>
            <select 
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="admin-filter-select"
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e0' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          
          <div className="admin-filter-group">
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>🔍 Search:</label>
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-filter-input"
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e0', width: '250px' }}
            />
          </div>
          
          {(selectedCategory || searchTerm) && (
            <button
              onClick={() => {
                setSelectedCategory('');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className="admin-clear-filters-btn"
              style={{ 
                padding: '8px 16px', 
                background: '#e2e8f0', 
                border: 'none', 
                borderRadius: '6px', 
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              ✕ Clear Filters
            </button>
          )}
        </div>

        {/* DataTable */}
        <DataTable
          title="Posts Management"
          columns={columns}
          data={posts}
          onAdd={handleAdd}
          renderActions={renderCustomActions}
          addButtonText="Create New Post"
          searchPlaceholder="Search in current page..."
        />

        {/* Pagination Component with Records Per Page */}
        {totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
          />
        )}

        {/* Modals */}
        <InfoModal
          show={showInfoModal}
          onHide={() => {
            setShowInfoModal(false);
            setSelectedPost(null);
          }}
          title="Post Details"
          data={selectedPost}
          fields={infoFields}
        />

        <DeleteConfirmation
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedPost(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Post"
          message={`Are you sure you want to delete "${selectedPost?.title}"?`}
        />

        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
}

export default PostList;